import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import svgUrl from "../../../../assets/icons/Tamilnadu-Assembly-NEW.svg";
import styles from "./Map.module.css";
import { constituencyToDistrict, districtColors, constituencyNames, constituencyUrls } from "./constituencyData";
import PropTypes from "prop-types";

const getConstituencyNumber = id => {
  if (!id) return null;
  if (/_\d+_$/.test(id)) return null;
  if (!id.startsWith("_x")) return null;
  const decoded = id.replace(/^_x([0-9A-Fa-f]{2})_/, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  if (/^\d+$/.test(decoded)) return parseInt(decoded, 10);
  return null;
};

const ElectionMapComponent = ({ mainelectionPage }) => {
  const mapRef = useRef(null);
  const [hoveredDistrict, setHoveredDistrict] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [hoveredConstituency, setHoveredConstituency] = useState(null);
  const useElementsRef = useRef(new Map());
  const originalViewBoxRef = useRef(null);
  const districtLabelsRef = useRef([]); // NEW
  const [svgLoaded, setSvgLoaded] = useState(false);
  const [tooltipFlip, setTooltipFlip] = useState(false);

  // Code 1: fetch-based SVG loading — unchanged
  useEffect(
    () => {
      (async () => {
        try {
          const response = await global.fetch(svgUrl);
          const svgText = await response.text();
          if (mapRef.current) {
            mapRef.current.innerHTML = svgText;
            setSvgLoaded(true);
          }
        } catch (err) {
          console.error("Failed to load SVG:", err);
        }
      })();
    },
    [svgUrl]
  );

  useEffect(
    () => {
      if (!svgLoaded || !mapRef.current) return;

      const svgElement = mapRef.current.querySelector("svg");
      if (!svgElement) return;

      originalViewBoxRef.current = svgElement.getAttribute("viewBox");

      const titleBlock = svgElement.querySelector("#Assembly_type");
      if (titleBlock) titleBlock.style.display = "none";
      const legendBlock = svgElement.querySelector("#Layer_16");
      if (legendBlock) legendBlock.style.display = "none";

      // Code 2: hide ALL text so we can replace with custom positioned labels
      svgElement.querySelectorAll("text").forEach(t => {
        t.style.display = "none";
      });

      // Color symbols by district
      svgElement.querySelectorAll("symbol").forEach(symbol => {
        const num = getConstituencyNumber(symbol.getAttribute("id"));
        if (num && constituencyToDistrict[num]) {
          const color = districtColors[constituencyToDistrict[num]];
          if (color) {
            symbol.querySelectorAll("polygon, path, rect, circle, ellipse").forEach(shape => {
              const f = shape.getAttribute("fill");
              if (f && f !== "none") shape.setAttribute("fill", color);
            });
          }
        }
      });

      // Build district -> use elements map
      const districtMap = new Map();
      svgElement.querySelectorAll("use").forEach(el => {
        const href = el.getAttribute("href") || el.getAttribute("xlink:href");
        if (!href || !href.startsWith("#")) return;
        const num = getConstituencyNumber(href.substring(1));
        if (num && constituencyToDistrict[num]) {
          const district = constituencyToDistrict[num];
          el.setAttribute("data-district", district);
          el.setAttribute("data-constituency", num);
          el.classList.add("map-entity");
          if (!districtMap.has(district)) districtMap.set(district, []);
          districtMap.get(district).push(el);
        }
      });

      useElementsRef.current = districtMap;

      // Code 2: screenToSvg helper
      const screenToSvg = (screenX, screenY) => {
        const pt = svgElement.createSVGPoint();
        pt.x = screenX;
        pt.y = screenY;
        const ctm = svgElement.getScreenCTM();
        if (!ctm) return null;
        return pt.matrixTransform(ctm.inverse());
      };

      // Code 2: create district labels at centroids
      const svgNS = "http://www.w3.org/2000/svg";
      const labels = [];

      districtMap.forEach((elements, districtName) => {
        let minX = Infinity;

        let minY = Infinity;

        let maxX = -Infinity;

        let maxY = -Infinity;

        elements.forEach(el => {
          try {
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) return;
            const tl = screenToSvg(rect.left, rect.top);
            const br = screenToSvg(rect.right, rect.bottom);
            if (!tl || !br) return;
            minX = Math.min(minX, tl.x, br.x);
            minY = Math.min(minY, tl.y, br.y);
            maxX = Math.max(maxX, tl.x, br.x);
            maxY = Math.max(maxY, tl.y, br.y);
          } catch (e) {
            /* ignore */
          }
        });

        if (minX === Infinity) return;

        let cx = (minX + maxX) / 2;
        let cy = (minY + maxY) / 2;

        if (districtName === "சென்னை") {
          cx = (76.515 + 154.439) / 2;
          cy = (103.884 + 125.589) / 2;
        }
        if (districtName === "திருவாரூர்") cy -= 15;
        if (districtName === "திருச்சிராப்பள்ளி") cy -= 25;
        if (districtName === "திருவள்ளூர்") cx += 25;
        if (districtName === "கோயம்புத்தூர்") cy -= 20;
        if (districtName === "கரூர்") cy -= 15;
        if (districtName === "நாகப்பட்டினம்") cx += 45;
        if (districtName === "இராணிப்பேட்டை") {
          cy -= 15;
          cx += 15;
        }
        if (districtName === "கிருஷ்ணகிரி") cx -= 15;
        if (districtName === "சிவகங்கை") cx += 25;
        if (districtName === "இராமநாதபுரம்") cy += 15;

        const label = document.createElementNS(svgNS, "text");
        label.setAttribute("x", cx);
        label.setAttribute("y", cy);
        label.setAttribute("text-anchor", "middle");
        label.setAttribute("dominant-baseline", "central");
        label.setAttribute("font-size", "14");
        label.setAttribute("font-weight", "600");
        label.setAttribute("fill", districtName === "சென்னை" ? "#FFFFFF" : "#1a1a2e");
        label.setAttribute("pointer-events", "none");
        label.setAttribute("class", "district-label");
        label.textContent = districtName;

        svgElement.appendChild(label);
        labels.push(label);
      });

      districtLabelsRef.current = labels;
    },
    [svgLoaded]
  );

  const clearHighlights = useCallback(() => {
    useElementsRef.current.forEach(elements => {
      elements.forEach(el => {
        el.style.filter = "";
        el.style.opacity = "";
      });
    });
  }, []);

  const highlightDistrict = useCallback(districtName => {
    useElementsRef.current.forEach((elements, district) => {
      elements.forEach(el => {
        if (district === districtName) {
          el.style.filter = "brightness(1.2) saturate(1.5)";
          el.style.opacity = "1";
        } else {
          el.style.filter = "";
          el.style.opacity = "0.4";
        }
      });
    });
  }, []);

  // Code 2: real viewBox zoom with screenToSvg bounding box
  const zoomToDistrict = useCallback(districtName => {
    const svgElement = mapRef.current.querySelector("svg");
    if (!svgElement) return;

    const elements = useElementsRef.current.get(districtName);
    if (!elements || elements.length === 0) return;

    const screenToSvg = (screenX, screenY) => {
      const pt = svgElement.createSVGPoint();
      pt.x = screenX;
      pt.y = screenY;
      const ctm = svgElement.getScreenCTM();
      if (!ctm) return null;
      return pt.matrixTransform(ctm.inverse());
    };

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    elements.forEach(el => {
      try {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;
        const tl = screenToSvg(rect.left, rect.top);
        const br = screenToSvg(rect.right, rect.bottom);
        if (!tl || !br) return;
        minX = Math.min(minX, tl.x, br.x);
        minY = Math.min(minY, tl.y, br.y);
        maxX = Math.max(maxX, tl.x, br.x);
        maxY = Math.max(maxY, tl.y, br.y);
      } catch (e) {
        /* ignore */
      }
    });

    if (minX === Infinity) return;

    const padding = Math.max(maxX - minX, maxY - minY) * 0.15;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Actually zoom the SVG viewBox
    svgElement.setAttribute("viewBox", `${minX} ${minY} ${maxX - minX} ${maxY - minY}`);

    useElementsRef.current.forEach((elems, district) => {
      elems.forEach(el => {
        if (district === districtName) {
          el.style.filter = "brightness(1.2) saturate(1.5)";
          el.style.opacity = "1";
          el.style.stroke = "#333";
          el.style.strokeWidth = "0.5";
        } else {
          el.style.filter = "";
          el.style.opacity = "0.3";
        }
      });
    });

    // Hide labels while zoomed in
    districtLabelsRef.current.forEach(label => {
      label.style.display = "none";
    });

    setSelectedDistrict(districtName);
    setHoveredDistrict(null);
  }, []);

  // Code 2: restore viewBox and labels on zoom out
  const zoomOut = useCallback(() => {
    const svgElement = mapRef.current.querySelector("svg");
    if (!svgElement || !originalViewBoxRef.current) return;

    svgElement.setAttribute("viewBox", originalViewBoxRef.current);

    useElementsRef.current.forEach(elements => {
      elements.forEach(el => {
        el.style.filter = "";
        el.style.opacity = "";
        el.style.stroke = "";
        el.style.strokeWidth = "";
      });
    });

    districtLabelsRef.current.forEach(label => {
      label.style.display = "";
    });

    setSelectedDistrict(null);
    setHoveredConstituency(null);
  }, []);

  const handleMouseMove = e => {
    const target = e.target.closest("[data-district]");
    const rect = mapRef.current.getBoundingClientRect();

    // Flip tooltip if cursor is in the right 40% of the container
    const relativeX = e.clientX - rect.left;
    setTooltipFlip(relativeX > rect.width * 0.6);

    setTooltipPos({ x: relativeX + 15, y: e.clientY - rect.top + 15 });

    if (!target) {
      if (hoveredDistrict && !selectedDistrict) {
        setHoveredDistrict(null);
        clearHighlights();
      }
      if (selectedDistrict) setHoveredConstituency(null);
      return;
    }
    setTooltipPos({ x: e.clientX - rect.left + 15, y: e.clientY - rect.top + 15 });
    if (selectedDistrict) {
      const num = parseInt(target.getAttribute("data-constituency"), 10);
      if (num !== hoveredConstituency) setHoveredConstituency(num);
    } else {
      const district = target.getAttribute("data-district");
      if (district !== hoveredDistrict) {
        setHoveredDistrict(district);
        highlightDistrict(district);
      }
    }
  };

  const handleMouseLeave = () => {
    if (!selectedDistrict) {
      setHoveredDistrict(null);
      clearHighlights();
    }
    setHoveredConstituency(null);
  };

  const handleClick = e => {
    const target = e.target.closest("[data-district]");
    if (!target || selectedDistrict) return;
    const district = target.getAttribute("data-district");
    if (district) zoomToDistrict(district);
  };

  // Code 2: useMemo replaces getDistrictConstituencies()
  const districtConstituencies = useMemo(
    () => {
      if (!selectedDistrict) return [];
      const result = [];
      for (const [num, district] of Object.entries(constituencyToDistrict)) {
        if (district === selectedDistrict) {
          let href = constituencyUrls[num];

          // Append source=app if current URL has it
          if (typeof global !== "undefined" && global.location) {
            const regExp = /source=app/;
            if (regExp.test(global.location.href)) {
              href += href.includes("?") ? "&source=app" : "?source=app";
            }
          }

          result.push({
            number: parseInt(num, 10),
            name: constituencyNames[num] || `Constituency ${num}`,
            url: href
          });
        }
      }
      return result.sort((a, b) => a.number - b.number);
    },
    [selectedDistrict]
  );

  const tooltipText = selectedDistrict
    ? hoveredConstituency
      ? `#${hoveredConstituency} - ${constituencyNames[hoveredConstituency] || ""}`
      : null
    : hoveredDistrict;

  return (
    <div className={`${styles["map-wrapper"]} ${mainelectionPage ? styles["main-election-page"] : ""}`}>
      <div
        className={`${styles["map-container"]} ${selectedDistrict ? styles["detail-view"] : ""}`}
        ref={mapRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {/* SVG injected dynamically via useEffect */}
        {tooltipText && (
          <div
            className={`${styles["tooltip"]} ${tooltipFlip ? styles["tooltip-left"] : ""}`}
            style={{ left: tooltipPos.x, top: tooltipPos.y }}
          >
            {tooltipText}
          </div>
        )}
      </div>

      {selectedDistrict && (
        <div className={styles["detail-panel"]}>
          <button className={styles["back-button"]} onClick={zoomOut}>
            ← Back to full map
          </button>
          <h2 className={styles["district-title"]}>{selectedDistrict}</h2>
          <p className={styles["constituency-count"]}>{districtConstituencies.length} constituencies</p>
          <ul className={styles["constituency-list"]}>
            {districtConstituencies.map(c => (
              <li
                key={c.number}
                className={`${styles["constituency-item"]} ${hoveredConstituency === c.number ? styles["active"] : ""}`}
              >
                <a href={c.url} target="_blank" rel="noopener noreferrer" className={styles["constituency-link"]}>
                  <span className={styles["constituency-number"]}>#{c.number}</span>
                  <span className={styles["constituency-name"]}>{c.name}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

ElectionMapComponent.propTypes = {
  mainelectionPage: PropTypes.bool
};

export default ElectionMapComponent;
